/**
 * VerkaufsEmpfehlungenWidget — Top-5-Verkaufs-Empfehlungen auf dem Dashboard.
 *
 * Zeigt Fahrzeuge mit dringendster Verkaufs-Empfehlung (sortiert nach
 * steuer_optimal_score DESC). Klick öffnet den Verkaufs-Tab des Fahrzeugs.
 * Widget ist nur sichtbar, wenn mind. 1 Empfehlung vorhanden.
 */

import { useEffect, useState } from 'react';
import { TrendingUp, Loader2, ArrowRight } from 'lucide-react';
import { useLocation } from 'wouter';
import { supabase } from '@/lib/supabase';

interface FahrzeugInfo {
  marke: string | null;
  modell: string | null;
  kennzeichen: string | null;
}

interface VerkaufsEmpfehlung {
  id: string;
  fahrzeug_id: string;
  steuer_optimal_score: number;
  empfohlenes_verkaufsdatum: string | null;
  geschaetzter_buchgewinn: number | null;
  // PostgREST returns FK join as single object or array depending on relationship direction
  fahrzeuge: FahrzeugInfo | FahrzeugInfo[] | null;
}

function getFahrzeugInfo(fahrzeuge: FahrzeugInfo | FahrzeugInfo[] | null): FahrzeugInfo | null {
  if (!fahrzeuge) return null;
  return Array.isArray(fahrzeuge) ? (fahrzeuge[0] ?? null) : fahrzeuge;
}

function formatBetrag(val: number | null): string {
  if (val === null) return '—';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDatum(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', { month: 'short', year: 'numeric' });
}

function ScoreBadge({ score }: { score: number }) {
  const bg = score > 85
    ? 'bg-red-100 text-red-700'
    : score > 70
      ? 'bg-orange-100 text-orange-700'
      : 'bg-yellow-50 text-yellow-700';
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${bg}`} data-testid="score-badge">
      {score}
    </span>
  );
}

function EmpfehlungZeile({
  empfehlung,
  onClick,
}: {
  empfehlung: VerkaufsEmpfehlung;
  onClick: () => void;
}) {
  const fz = getFahrzeugInfo(empfehlung.fahrzeuge);
  const label = [fz?.marke, fz?.modell].filter(Boolean).join(' ') || '—';
  const kennzeichen = fz?.kennzeichen ?? null;
  const buchgewinn = empfehlung.geschaetzter_buchgewinn;

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2.5 py-2 -mx-2 px-2 rounded-lg hover:bg-gray-50 transition-colors group text-left"
      data-testid={`empfehlung-zeile-${empfehlung.fahrzeug_id}`}
    >
      <ScoreBadge score={empfehlung.steuer_optimal_score} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-gray-800 truncate">{label}</span>
          {kennzeichen && (
            <span className="text-[10px] font-mono text-gray-400 shrink-0">{kennzeichen}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] font-medium ${buchgewinn !== null && buchgewinn >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {buchgewinn !== null && buchgewinn >= 0 ? '+' : ''}{formatBetrag(buchgewinn)}
          </span>
          {empfehlung.empfohlenes_verkaufsdatum && (
            <span className="text-[10px] text-gray-400">
              bis {formatDatum(empfehlung.empfohlenes_verkaufsdatum)}
            </span>
          )}
        </div>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-500 transition-colors shrink-0" />
    </button>
  );
}

function useVerkaufsEmpfehlungen() {
  const [empfehlungen, setEmpfehlungen] = useState<VerkaufsEmpfehlung[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Get the latest recommendation per vehicle: subquery via window isn't available in PostgREST,
        // so we fetch recent ones (last 14 days) and deduplicate client-side.
        const cutoff = new Date(Date.now() - 14 * 86_400_000).toISOString().substring(0, 10);
        const { data, error } = await supabase
          .from('fahrzeug_verkaufs_empfehlungen')
          .select('id, fahrzeug_id, steuer_optimal_score, empfohlenes_verkaufsdatum, geschaetzter_buchgewinn, fahrzeuge(marke, modell, kennzeichen)')
          .gte('berechnet_am', cutoff)
          .gt('steuer_optimal_score', 0)
          .order('steuer_optimal_score', { ascending: false })
          .limit(20);

        if (!cancelled && !error && data) {
          // Deduplicate: keep highest score per fahrzeug_id
          const seen = new Set<string>();
          const deduped = (data as unknown as VerkaufsEmpfehlung[]).filter(e => {
            if (seen.has(e.fahrzeug_id)) return false;
            seen.add(e.fahrzeug_id);
            return true;
          });
          setEmpfehlungen(deduped.slice(0, 5));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { empfehlungen, loading };
}

export default function VerkaufsEmpfehlungenWidget() {
  const { empfehlungen, loading } = useVerkaufsEmpfehlungen();
  const [, setLocation] = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (empfehlungen.length === 0) return null;

  return (
    <div data-testid="verkaufs-empfehlungen-widget">
      <div className="space-y-0.5">
        {empfehlungen.map(emp => (
          <EmpfehlungZeile
            key={emp.id}
            empfehlung={emp}
            onClick={() => setLocation(`/fahrzeuge/${emp.fahrzeug_id}/verkauf`)}
          />
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-1 text-[10px] text-gray-400">
        <TrendingUp className="w-3 h-3" />
        Score = Dringlichkeit · &gt;70 = Empfehlung aktiv
      </div>
    </div>
  );
}
